import AmountReceivable from '@components/amount-receivable/amount-receivable.component'
import CardPaymentData from '@components/card-payment-data/card-payment-data.component'
import { withTransaction } from '@elastic/apm-rum-react'
import {
  ButtonBack,
  ButtonCopyText,
  FinancialOperationService,
  IncompletePaymentData,
  InfluencerTransaction,
  Loader,
  LogService,
  StringsHelper,
  ToastHelper,
  User,
  localForageHelper,
  onLangChange,
  onUserChange,
} from '@hub/shared'
import Extract from '@pages/payments-details/components/extract/extract.component'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { SubscriptionLike } from 'rxjs'
import './payments-details.page.scoped.scss'
import './payments-details.page.scss'
import { AxiosError } from 'axios'
import TransactionDetails from '@src/components/transaction-details/transaction-details.component'

const PaymentsDetailsPageComponent = () => {
  const onLangChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const stringsHelper = useMemo(() => new StringsHelper(), [])
  const { transactionId } = useParams<{ transactionId: string }>()
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const financialService = useMemo(() => new FinancialOperationService(), [])
  const logService = useMemo(() => new LogService(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])

  const [t] = useTranslation('paymentsData')
  const navigate = useNavigate()

  const [lang, setLang] = useState<string>('en')
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [transactionObject, setTransactionObject] = useState<InfluencerTransaction | null>(null)
  const [hideValue, setHideValue] = useState(true)
  const [impersonateObject, setImpersonateObject] = useState<any>(null)
  const [hideData, setHideData] = useState(false)

  useEffect(() => {
    onLangChangeSubscription.current = onLangChange.subscribe((lang) => {
      setLang(lang)
    })

    return () => {
      onLangChangeSubscription.current?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    localForageHelper.getItem('impersonateUser').then((impersonateUser) => setImpersonateObject(impersonateUser))
  }, [])

  useEffect(() => {
    setHideData(!!impersonateObject && !impersonateObject?.impersonateAdmin)
  }, [impersonateObject])

  const getTransactionDetails = useCallback(() => {
    setLoading(true)
    financialService
      .getTransactionDetails(transactionId || '')
      .then((e: InfluencerTransaction | null) => {
        setTransactionObject(e)
      })
      .catch((error: AxiosError) => {
        logService.error({ message: 'Failed load details transaction', error })
        toast.error(t('paymentsDetails.unexpected'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [financialService, transactionId, t, toast, logService])

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe(async (user: User) => {
      if (user?.squidId) {
        setUser(user)
        getTransactionDetails()
      }
    })

    return () => {
      onUserChangeSubscription?.current?.unsubscribe()
    }
  }, [onUserChangeSubscription, getTransactionDetails])

  const Header = () => {
    if (transactionObject) {
      return (
        <header>
          <ButtonBack circleLayout onReferrerFalseUrl="/payments/info" />
          <h4 className="m-0">
            {stringsHelper.capitalizeFirstLetter(
              new Date(transactionObject?.transaction?.dueDate)?.toLocaleString(lang, { month: 'long' })
            )}{' '}
            {transactionObject?.transaction?.dueDate.substring(0, 4)}
          </h4>
        </header>
      )
    }
    return (
      <header>
        <ButtonBack circleLayout onReferrerFalseUrl="/payments/info" />
      </header>
    )
  }

  return (
    <section className="payment-details-page box">
      {loading ? (
        <div className="text-center my-5" style={{ width: '100%' }}>
          <Loader size="big" />
        </div>
      ) : (
        <div className="content">
          <Header />
          <AmountReceivable
            transactionObject={transactionObject}
            onChange={(value) => setHideValue(value)}
            updateTransaction={() => getTransactionDetails()}
          />
          <Extract transactionObject={transactionObject} hideValue={hideValue} />
          {user?.profileadditionalinfo?.nationality === 'BR' ? (
            <div>
              <hr />
              <div className="my-5">
                {!user?.banks?.[0]?.id ? (
                  <IncompletePaymentData redirect={(path) => navigate(path)} />
                ) : (
                  <div>
                    <h5>{t('pageTitle')}</h5>
                    <CardPaymentData
                      canEdit={false}
                      hideData={hideData}
                      seeData={() => navigate('/payments/data')}
                      transactionObject={transactionObject}
                      hideQipu
                    />
                  </div>
                )}
              </div>
            </div>
          ) : null}
          <div>
            <hr />
            <div className="my-5">
              <h5>ID Wake Creators</h5>
              <div className="card-id-squid">
                <i className="fa-regular fa-memo-pad gray-light" style={{ fontSize: '2.5rem' }}></i>
                <ButtonCopyText
                  content={transactionObject?.transaction?.transactionId}
                  backgroundColor="transparent"
                  textColor="var(--gray)"
                  noMargin
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default withTransaction('PaymentsDetailsPageComponent', 'component')(PaymentsDetailsPageComponent)
